import type {
  ConsentModalSection,
  CookieCategories,
  CookieConsentConfig,
} from './types.ts'

import type { GetSiteConfigApiResponse } from '#src/api-handlers/types.ts'
import type { CookieConsentTranslator } from '#src/assets/lib/i18n.ts'
import type { Logger } from '#src/assets/lib/logger.ts'
import type { SiteConfig } from '#src/models/site-configs/siteConfigs.ts'
import {
  COOKIE_CATEGORIES,
  type CookieCategory,
  type CookieRecord,
} from '~/src/services/cookieTranslationService/types.ts'

const VALID_COOKIE_CATEGORIES = new Set<string>([
  'necessary',
  'analytics',
  'marketing',
  'personalization',
  'security',
  'uncategorized',
])

function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export function getCategoryTitle({
  category,
  t,
}: {
  category: CookieCategory
  t: CookieConsentTranslator
}): string {
  const translationKey: `${CookieCategory}CategoryTitle` = `${category}CategoryTitle`
  const translatedTitle = t(translationKey)

  if (translatedTitle === translationKey) {
    return capitalizeFirst(category)
  }

  return translatedTitle
}

export function getCategoryDescription({
  category,
  t,
}: {
  category: CookieCategory
  t: CookieConsentTranslator
}): string {
  const translationKey: `${CookieCategory}CategoryDescription` = `${category}CategoryDescription`
  const translatedDescription = t(translationKey)

  if (translatedDescription === translationKey) {
    return `${capitalizeFirst(category)} cookies`
  }

  return translatedDescription
}

function buildConsentModalSections({
  siteConfig,
  cookiesByCategory,
  declaredCategories,
  t,
}: {
  siteConfig: SiteConfig
  cookiesByCategory: Record<CookieCategory, Array<CookieRecord>>
  declaredCategories: CookieCategory[]
  t: CookieConsentTranslator
}): ConsentModalSection[] {
  const sections: ConsentModalSection[] = [
    {
      title: t('preferencesIntroTitle'),
      description: t('preferencesIntroDescription', {
        coachName: siteConfig.coachName,
        policyUrl: siteConfig.cookiePolicyUrl,
      }),
    },
    {
      title: t('necessaryCategoryTitle'),
      description: t('necessaryCategoryDescription'),
      linkedCategory: 'necessary',
    },
  ]

  getConfiguredCategories(cookiesByCategory, declaredCategories).forEach(
    category => {
      if (category === 'necessary') {
        return
      }
      sections.push({
        title: getCategoryTitle({ category, t }),
        description: getCategoryDescription({ category, t }),
        linkedCategory: category,
      })
    },
  )

  return sections
}

export function buildConsentsObject({
  acceptedCategories,
  allCategories,
}: {
  acceptedCategories: string[]
  allCategories: CookieCategories
}): Record<string, boolean> {
  return Object.fromEntries(
    Object.keys(allCategories).map(category => [
      category,
      acceptedCategories.includes(category),
    ]),
  )
}

export function getCookieDomainFromWebsiteUrl(websiteUrl: string): string {
  const normalizeUrl = (url: string): string => {
    return `.${url.trim().toLowerCase()}`
  }

  try {
    const hostname = new URL(websiteUrl.trim()).hostname.toLowerCase()
    return normalizeUrl(hostname)
  } catch {
    return normalizeUrl(websiteUrl)
  }
}

function normalizeCookieCategory(category: string): CookieCategory {
  const normalizedCategory = category.trim().toLowerCase()

  if (VALID_COOKIE_CATEGORIES.has(normalizedCategory)) {
    return normalizedCategory as CookieCategory
  }

  return 'uncategorized'
}

function createEmptyCookieCategoryMap(): Record<
  CookieCategory,
  Array<CookieRecord>
> {
  return {
    necessary: [],
    marketing: [],
    analytics: [],
    uncategorized: [],
    security: [],
    personalization: [],
  }
}

export function getConfiguredCategories(
  cookiesByCategory: Record<CookieCategory, Array<CookieRecord>>,
  declaredCategories: CookieCategory[] = [],
): CookieCategory[] {
  const declaredCategorySet = new Set(declaredCategories)

  return COOKIE_CATEGORIES.filter(
    category =>
      cookiesByCategory[category].length > 0 ||
      declaredCategorySet.has(category),
  )
}

export function getCookiesByCategory({
  apiConfig,
}: {
  apiConfig: GetSiteConfigApiResponse
}): Record<CookieCategory, Array<CookieRecord>> {
  const { cookies } = apiConfig
  const cookiesByCategory = createEmptyCookieCategoryMap()

  for (const cookie of cookies) {
    const category = normalizeCookieCategory(cookie.category)
    cookiesByCategory[category].push({
      ...cookie,
      category,
    })
  }

  return cookiesByCategory
}

export function buildCookieConsentConfig({
  apiConfig,
  cookiesByCategory,
  declaredCategories = [],
  locale,
  t,
  onConsentUpdated,
  log,
}: {
  apiConfig: GetSiteConfigApiResponse
  cookiesByCategory: Record<CookieCategory, Array<CookieRecord>>
  declaredCategories?: CookieCategory[]
  locale: string
  t: CookieConsentTranslator
  onConsentUpdated: (consents: Record<string, boolean>, version: string) => void
  log: Logger['log']
}): CookieConsentConfig {
  const { config: siteConfig } = apiConfig
  const localeKey = locale.split('-')[0] || 'en'
  const configuredCategories = getConfiguredCategories(
    cookiesByCategory,
    declaredCategories,
  )
  const categories: CookieCategories = {
    necessary: {
      enabled: true,
      readOnly: true,
    },
  }

  configuredCategories.forEach(category => {
    if (category === 'necessary') {
      return
    }

    categories[category] = {
      enabled: false,
      readOnly: false,
    }
  })

  const sections = buildConsentModalSections({
    siteConfig,
    cookiesByCategory,
    declaredCategories,
    t,
  })

  const handleConsent = (acceptedCategories: string[]): void => {
    const consents = buildConsentsObject({
      acceptedCategories,
      allCategories: categories,
    })

    onConsentUpdated(consents, siteConfig.version)
  }

  return {
    autoShow: false,
    categories,
    version: siteConfig.version,
    cookie: {
      name: 'cc_cookie',
      expiresAfterDays: 182,
      sameSite: 'Lax',
      secure: true,
      useLocalStorage: false,
    },
    language: {
      default: localeKey,
      autoDetect: 'document',
      translations: {
        [localeKey]: {
          consentModal: {
            title: t('consentModalTitle'),
            description: t('consentModalDescription', {
              policyUrl: siteConfig.cookiePolicyUrl,
              coachName: siteConfig.coachName,
            }),
            acceptAllBtn: t('acceptAllBtn'),
            acceptNecessaryBtn: t('acceptNecessaryBtn'),
            showPreferencesBtn: t('showPreferencesBtn'),
            footer: `<a href="${siteConfig.cookiePolicyUrl}" target="_blank" rel="noopener noreferrer">${t('cookiePolicy')}</a>`,
          },
          preferencesModal: {
            title: t('preferencesModalTitle'),
            acceptAllBtn: t('acceptAllBtn'),
            acceptNecessaryBtn: t('acceptNecessaryBtn'),
            savePreferencesBtn: t('savePreferencesBtn'),
            closeIconLabel: t('closeIconLabel'),
            sections,
          },
        },
      },
    },
    guiOptions: {
      consentModal: {
        layout: 'box wide',
        position: 'bottom left',
        flipButtons: false,
        equalWeightButtons: true,
      },
      preferencesModal: {
        layout: 'box',
        position: 'right',
        flipButtons: false,
        equalWeightButtons: true,
      },
    },
    onFirstConsent: ({ cookie }) => {
      log('First consent given:', cookie.categories)
      handleConsent(cookie.categories)
    },
    onChange: ({ cookie, changedCategories }) => {
      log('Consent changed:', changedCategories)
      log('Cookie data from onChange:', cookie)
      handleConsent(cookie.categories)
    },
  }
}
